import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import ArrowExpand from '@uber/base-icons/arrow-expand';
import ArrowShrink from '@uber/base-icons/arrow-shrink';
import ChevronLeft from '@uber/base-icons/chevron-left';
import ChevronRight from '@uber/base-icons/chevron-right';
import PlayerNext from '@uber/base-icons/player-next';
import PlayerPauseFilled from '@uber/base-icons/player-pause';
import PlayerPlayFilled from '@uber/base-icons/player-play-filled';
import PlayerPrevious from '@uber/base-icons/player-previous';
import { styled } from 'baseui';
import { Input, SIZE as INPUT_SIZE } from 'baseui/input';
import { type Option, Select, SIZE as SELECT_SIZE } from 'baseui/select';
import { LabelSmall } from 'baseui/typography';
import { useAtomValue } from 'jotai';
import isNil from 'lodash/isNil';
import isNumber from 'lodash/isNumber';
import throttle from 'lodash/throttle';
import { useDOMWidth } from '../../../../common/hooks/use-dom-width';
import { useTrackInteractionLatency } from '../../../../common/hooks/use-track-interaction-latency';
import { IconButton } from '../../../../common/player-controls/icon-button';
import { VolumeSlider } from '../../../../common/player-controls/volume-slider';
import useAnalyticsForVideoLabelling from '../../../labelling/components/frames-control-panel/analytics/useAnalyticsForVideoLabelling';
import { FRAME_CONTROL_MODE } from '../../../labelling/components/frames-control-panel/constants';
import useShortcut from '../../../labelling/providers/hooks/use-shortcut';
import { handleShortcutRegistrationError, SUPPORTED_KEYBOARD_EVENTS } from '../../../labelling/providers/utils/shortcut';
import type { VideoPlayerControlTypesT } from '../../../labelling/types';
import { isValidInputTimeFormatController } from '../../../labelling/utils';
import { TimeDisplayMode } from '../../../types';
import { getFrameToTime, getTimeToFrame } from '../../../utils/frame-time';
import { selectedVideoIndexAtom } from '../../atoms/multi-video-atoms';
import { PERFORMANCE_METRIC_EVENTS } from '../../constants/performance';
import { usePerformanceMetrics } from '../../hooks/performance/use-performance-metrics';
import { BUTTON_SIZE, FRAME_STEP_SIZE, SPEED_OPTIONS, VIDEO_PLAYER_CONTROL_TYPES } from './constants';
import { FrameSlider } from './frame-slider';
import { getInputOverridesWithWidth, SELECT_OVERRIDES } from './overrides';
import { SwitchFrameTimeButton } from './switch-frame-time-button';
const SPEED_SELECT_OPTIONS: Option[] = SPEED_OPTIONS.map(speed => ({
  label: `${speed}x`,
  id: speed
}));
const SliderContainer = styled('div', () => ({
  width: '154px'
}));
const VideoControls = styled('div', () => ({
  display: 'flex'
}));
const FramesControlPanelSection = styled('div', ({
  $theme
}) => ({
  display: 'flex',
  alignItems: 'center',
  gap: $theme.sizing.scale600
}));
const InputPair = styled('div', ({
  $theme
}) => ({
  display: 'flex',
  alignItems: 'center',
  gap: $theme.sizing.scale300
}));
const Controls = styled('div', ({
  $theme
}) => ({
  display: 'flex',
  flexDirection: 'row',
  justifyContent: 'space-between',
  width: 'auto',
  borderTop: `${$theme.sizing.scale0} solid ${$theme.colors.borderOpaque}`,
  borderBottom: `${$theme.sizing.scale0} solid ${$theme.colors.borderOpaque}`,
  backgroundColor: $theme.colors.backgroundPrimary,
  height: $theme.sizing.scale950,
  padding: `${$theme.sizing.scale200} ${$theme.sizing.scale500}`
}));
const StyledLabelMedium = styled('div', ({
  $theme
}) => ({
  ...$theme.typography.LabelMedium,
  color: $theme.colors.contentTertiary
}));

// Hidden span used to measure the visual width of text with the same typography
const HiddenMeasure = styled('span', ({
  $theme
}) => ({
  ...$theme.typography.LabelMedium,
  position: 'absolute',
  visibility: 'hidden',
  whiteSpace: 'pre',
  padding: 0,
  margin: 0,
  height: 0,
  overflow: 'hidden'
}));
type PropsT = {
  isPlaying: boolean;
  togglePlayback: (playbackStatus: boolean) => void;
  playSpeed: number;
  setPlaySpeed: (number) => void;
  mediaVolume?: number;
  /**
   * @param volume - Volume value between 0 and 1
   * @returns void
   */
  onMediaVolumeChange?: (volume: number) => void;
  timeDisplayMode: TimeDisplayMode;
  setTimeDisplayMode: (timeDisplayMode: TimeDisplayMode) => void;
  currentFrame: number;
  maxFrames: number;
  imageLoadStatus: boolean[];
  onFrameChange: (frame: number) => void;
  frameRate: number | null | undefined;
  mode: (typeof FRAME_CONTROL_MODE)[keyof typeof FRAME_CONTROL_MODE];
  isTimelineExpanded: boolean;
  isVideoPlayer: boolean;
  onIsTimelineExpandedChange: (isExpanded: boolean) => void;
  isDisabled?: boolean;
  /** The active HTMLVideoElement. Passed reactively from the provider so that
   *  play/pause/seek latency listeners attach after the element mounts. */
  mediaElement: HTMLVideoElement | null;
};
export const FramesControls = ({
  isPlaying,
  togglePlayback,
  playSpeed,
  setPlaySpeed,
  mediaVolume,
  onMediaVolumeChange,
  imageLoadStatus,
  timeDisplayMode,
  setTimeDisplayMode,
  currentFrame: currentFrameProp,
  onFrameChange,
  maxFrames,
  frameRate,
  mode,
  isTimelineExpanded,
  isVideoPlayer,
  onIsTimelineExpandedChange,
  isDisabled,
  mediaElement
}: PropsT) => {
  const [sliderValue, setSliderValue] = useState<[number]>([currentFrameProp]);
  const isSliderDragging = useRef(false);
  const onFrameChangeRef = useRef(onFrameChange);
  // eslint-disable-next-line react-hooks/refs
  onFrameChangeRef.current = onFrameChange;
  const throttledOnFrameChange = useMemo(() => throttle(onFrameChange, 33, {
    leading: true,
    trailing: true
  }),
  // ~ 30fps
  [onFrameChange]);
  const [currentFrameLocal, setCurrentFrameLocal] = useState<string>(String(currentFrameProp + 1));
  const defaultCurrentTime = getFrameToTime({
    frame: currentFrameProp,
    maxFrames,
    frameRate
  });
  const [currentTimeLocal, setCurrentTimeLocal] = useState<string>(defaultCurrentTime);
  const {
    registerHotKeys
  } = useShortcut();
  const {
    sendEventVideoPlayerControls,
    sendEventVideoFrameNumberInput
  } = useAnalyticsForVideoLabelling();
  const currentTimeValid = isValidInputTimeFormatController(currentTimeLocal, maxFrames, getTimeToFrame(currentTimeLocal, frameRate));
  const currentFrameLocalNumber = Number(currentFrameLocal) - 1;
  const currentFrameValid = currentFrameLocal === '' || currentFrameLocalNumber >= 0 && currentFrameLocalNumber < maxFrames;
  useEffect(() => {
    setSliderValue([currentFrameProp]);
    setCurrentFrameLocal(String(currentFrameProp + 1));
    setCurrentTimeLocal(getFrameToTime({
      frame: currentFrameProp,
      maxFrames,
      frameRate
    }));
  }, [currentFrameProp, maxFrames, frameRate]);
  const isCurrentFrameLoaded = imageLoadStatus[currentFrameProp];
  const isNextFrameLoaded = currentFrameProp + 1 < imageLoadStatus.length ? imageLoadStatus[currentFrameProp + 1] : true;
  const isFrameLoaded = isCurrentFrameLoaded && isNextFrameLoaded;
  const showPlayPauseButton = isFrameLoaded || isVideoPlayer;
  const isCompactMode = mode === FRAME_CONTROL_MODE.COMPACT;
  const selectedVideoIndex = useAtomValue(selectedVideoIndexAtom);
  const isMultiVideoIndex = selectedVideoIndex !== 0;
  const selectedSpeed = SPEED_SELECT_OPTIONS.find(opt => opt.id === playSpeed);
  const maxTime = getFrameToTime({
    frame: maxFrames - 1,
    maxFrames,
    frameRate
  });
  const {
    width: maxFrameWidth,
    measureRef: maxFrameMeasureRef
  } = useDOMWidth();
  const {
    width: maxTimeWidth,
    measureRef: maxTimeMeasureRef
  } = useDOMWidth();
  const frameInputOverrides = useMemo(() => getInputOverridesWithWidth(maxFrameWidth), [maxFrameWidth]);
  const timeInputOverrides = useMemo(() => getInputOverridesWithWidth(maxTimeWidth), [maxTimeWidth]);
  const maxFramesMax = String(maxFrames).replace(/[0-9]/g, '0');
  const maxTimeMax = String(maxTime).replace(/[0-9]/g, '0');
  const {
    captureLatency
  } = usePerformanceMetrics();
  const stopPlaying = useTrackInteractionLatency(() => {
    togglePlayback(false);
  }, mediaElement, {
    metricName: PERFORMANCE_METRIC_EVENTS.VIDEO_PAUSE_TO_PAINT,
    event: 'pause',
    captureLatency
  });
  const startPlaying = useTrackInteractionLatency(() => {
    togglePlayback(true);
  }, mediaElement, {
    metricName: PERFORMANCE_METRIC_EVENTS.VIDEO_PLAY_TO_PAINT,
    event: 'play',
    captureLatency
  });
  const pausePlaybackIfPlaying = useCallback(() => {
    if (isPlaying) {
      stopPlaying();
    }
  }, [isPlaying, stopPlaying]);
  const handleSliderChange = useCallback(({
    value
  }: {
    value: [number];
  }) => {
    isSliderDragging.current = true;
    setSliderValue(value);
    throttledOnFrameChange(value[0]);
  }, [throttledOnFrameChange]);
  const handleSliderFinal = useCallback(({
    value
  }: {
    value: [number];
  }) => {
    isSliderDragging.current = false;
    setSliderValue(value);
    throttledOnFrameChange.cancel(); // flush pending updates
    onFrameChangeRef.current(value[0]);
  }, [throttledOnFrameChange]);
  const handleFrameInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setCurrentFrameLocal(value);
    const frameNum = Number(value) - 1;
    if (!isNumber(frameNum)) return;
    const isValid = value !== '' && frameNum >= 0 && frameNum < maxFrames;
    if (isValid) {
      onFrameChange(frameNum);
    }
  };
  const handleTimeInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newTime = e.target.value;
    setCurrentTimeLocal(newTime);
    const isValid = isValidInputTimeFormatController(newTime, maxFrames, getTimeToFrame(newTime, frameRate));
    if (isValid) {
      const frameNum = getTimeToFrame(newTime, frameRate);
      onFrameChange(frameNum);
    }
  };
  const handlePlaybackControlsClick = useCallback((controlType: VideoPlayerControlTypesT) => {
    pausePlaybackIfPlaying();
    sendEventVideoPlayerControls({
      action: controlType
    });
    switch (controlType) {
      case VIDEO_PLAYER_CONTROL_TYPES.SKIP_TO_FIRST:
        currentFrameProp !== 0 && onFrameChange(0);
        break;
      case VIDEO_PLAYER_CONTROL_TYPES.BACKWARD:
        currentFrameProp > 0 && onFrameChange(currentFrameProp - FRAME_STEP_SIZE.DEFAULT);
        break;
      case VIDEO_PLAYER_CONTROL_TYPES.FORWARD:
        currentFrameProp < maxFrames - FRAME_STEP_SIZE.DEFAULT && onFrameChange(currentFrameProp + FRAME_STEP_SIZE.DEFAULT);
        break;
      case VIDEO_PLAYER_CONTROL_TYPES.SKIP_TO_LAST:
        currentFrameProp !== maxFrames - 1 && onFrameChange(maxFrames - 1);
        break;
      case VIDEO_PLAYER_CONTROL_TYPES.PLAY:
        currentFrameProp === maxFrames - 1 && onFrameChange(0);
        startPlaying();
        break;
      case VIDEO_PLAYER_CONTROL_TYPES.JUMP_FORWARD:
        if (currentFrameProp === maxFrames - 1) break;
        let framesToJumpFwd = FRAME_STEP_SIZE.TEN_FRAMES;
        // if on first frame, jump 9 frames instead of 10 as frame numbering on UI is 1,10,20
        if (currentFrameProp === 0) {
          framesToJumpFwd = FRAME_STEP_SIZE.TEN_FRAMES - 1;
        }
        const finalFrameFwd = Math.min(maxFrames - 1, currentFrameProp + framesToJumpFwd);
        onFrameChange(finalFrameFwd);
        break;
      case VIDEO_PLAYER_CONTROL_TYPES.JUMP_BACKWARD:
        if (currentFrameProp === 0) break;
        let framesToJumpBack = FRAME_STEP_SIZE.TEN_FRAMES;
        // if 10th frame, jump 9 frames instead of 10 as frame numbering on UI is 1,10,20
        if (currentFrameProp === FRAME_STEP_SIZE.TEN_FRAMES - 1) {
          framesToJumpBack = FRAME_STEP_SIZE.TEN_FRAMES - 1;
        }
        const finalFrameBack = Math.max(0, currentFrameProp - framesToJumpBack);
        onFrameChange(finalFrameBack);
        break;
      default:
        break;
    }
  }, [pausePlaybackIfPlaying, currentFrameProp, onFrameChange, maxFrames, startPlaying, sendEventVideoPlayerControls]);
  const handleSwitchFrameTimeClick = () => {
    setTimeDisplayMode(timeDisplayMode === TimeDisplayMode.FRAMES ? TimeDisplayMode.TIMESTAMP : TimeDisplayMode.FRAMES);
  };
  const skipToFirstButton = () => <IconButton data-testid="start-frame" onClick={() => !isDisabled && handlePlaybackControlsClick(VIDEO_PLAYER_CONTROL_TYPES.SKIP_TO_FIRST)} disabled={isDisabled}>
      <PlayerPrevious title="Starting frame" size={BUTTON_SIZE} />
    </IconButton>;
  const previousFrameButton = () => <IconButton data-testid="prev-frame" onClick={e => {
    const videoPlayerControlType: VideoPlayerControlTypesT = (e.nativeEvent as MouseEvent)?.shiftKey ? VIDEO_PLAYER_CONTROL_TYPES.JUMP_BACKWARD : VIDEO_PLAYER_CONTROL_TYPES.BACKWARD;
    !isDisabled && handlePlaybackControlsClick(videoPlayerControlType);
  }} disabled={isDisabled}>
      <ChevronLeft title="Previous frame" size={BUTTON_SIZE} />
    </IconButton>;
  const playPauseButton = () => <IconButton data-testid="toggle-play-btn" onClick={() => !isDisabled && handlePlaybackControlsClick(isPlaying ? VIDEO_PLAYER_CONTROL_TYPES.PAUSE : VIDEO_PLAYER_CONTROL_TYPES.PLAY)} disabled={isDisabled}>
      {isPlaying ? <PlayerPauseFilled title="Pause" size={BUTTON_SIZE} /> : <PlayerPlayFilled title="Play" size={BUTTON_SIZE} />}
    </IconButton>;
  const loadingButton = () => <IconButton isLoading={true} />;
  const nextFrameButton = () => <IconButton data-testid="next-frame" onClick={e => {
    const videoPlayerControlType: VideoPlayerControlTypesT = (e.nativeEvent as MouseEvent).shiftKey ? VIDEO_PLAYER_CONTROL_TYPES.JUMP_FORWARD : VIDEO_PLAYER_CONTROL_TYPES.FORWARD;
    !isDisabled && handlePlaybackControlsClick(videoPlayerControlType);
  }} disabled={isDisabled}>
      <ChevronRight title="Next frame" size={BUTTON_SIZE} />
    </IconButton>;
  const skipToLastButton = () => <IconButton data-testid="last-frame" onClick={() => {
    !isDisabled && handlePlaybackControlsClick(VIDEO_PLAYER_CONTROL_TYPES.SKIP_TO_LAST);
  }} disabled={isDisabled}>
      <PlayerNext title="Last frame" size={BUTTON_SIZE} />
    </IconButton>;
  const frameSlider = () => <SliderContainer>
      <FrameSlider maxFrames={maxFrames} sliderValue={sliderValue} isDisabled={!!isDisabled} handleSliderChange={handleSliderChange} handleSliderFinal={handleSliderFinal} />
    </SliderContainer>;
  const volumeSlider = () => !isNil(mediaVolume) && onMediaVolumeChange ? <VolumeSlider volume={mediaVolume} onVolumeChange={onMediaVolumeChange} /> : null;
  const speedSelect = () => <InputPair>
      <Select overrides={SELECT_OVERRIDES} size={SELECT_SIZE.compact} clearable={false} options={SPEED_SELECT_OPTIONS} value={selectedSpeed ? [selectedSpeed] : []} onChange={({
      value
    }) => {
      // @ts-expect-error todo(ts-migration) TS2532 Object is possibly 'undefined'.
      setPlaySpeed((value as Option[])[0].id);
    }} />
    </InputPair>;
  const frameTimeInputSwitcher = () => <SwitchFrameTimeButton timeDisplayMode={timeDisplayMode} onClick={handleSwitchFrameTimeClick} />;
  const frameTimeInput = () => {
    if (timeDisplayMode === TimeDisplayMode.FRAMES) {
      return <InputPair>
          <LabelSmall>Frame</LabelSmall>
          <Input value={currentFrameLocal} onChange={handleFrameInputChange} aria-label="Current frame" size={INPUT_SIZE.mini} overrides={frameInputOverrides} error={!currentFrameValid} onBlur={() => sendEventVideoFrameNumberInput({
          enteredFrame: currentFrameLocalNumber
        })} />
          <StyledLabelMedium>{`/ ${maxFrames}`}</StyledLabelMedium>
        </InputPair>;
    }
    return <InputPair>
        <LabelSmall>Time</LabelSmall>
        <Input value={currentTimeLocal} onChange={handleTimeInputChange} size={INPUT_SIZE.mini} placeholder="Current time" overrides={timeInputOverrides} error={!currentTimeValid} />
        <StyledLabelMedium>{`/ ${maxTime}`}</StyledLabelMedium>
      </InputPair>;
  };
  const expandContractTimelineButton = () => <IconButton data-testid="expand-contract-timeline-btn" onClick={() => {
    onIsTimelineExpandedChange(!isTimelineExpanded);
  }} disabled={isDisabled}>
      {isTimelineExpanded ? <ArrowShrink title="Contract timeline" size={BUTTON_SIZE} /> : <ArrowExpand title="Expand timeline" size={BUTTON_SIZE} />}
    </IconButton>;
  useEffect(() => {
    const {
      errors,
      removeHotKeys
    } = registerHotKeys([{
      // toggle playback on key release
      type: SUPPORTED_KEYBOARD_EVENTS.KEYUP,
      keys: [' '],
      onAction: e => {
        e.preventDefault();
        togglePlayback(!isPlaying);
      }
    }, {
      keys: ['m'],
      onAction: e => {
        e.preventDefault();
        if (isMultiVideoIndex) {
          return;
        }
        !isDisabled && handlePlaybackControlsClick(VIDEO_PLAYER_CONTROL_TYPES.BACKWARD);
      }
    }, {
      keys: ['n'],
      onAction: e => {
        e.preventDefault();
        if (isMultiVideoIndex) {
          return;
        }
        !isDisabled && handlePlaybackControlsClick(VIDEO_PLAYER_CONTROL_TYPES.FORWARD);
      }
    }, {
      keys: ['Mod', 'ArrowLeft'],
      onAction: e => {
        e.preventDefault();
        if (isMultiVideoIndex) {
          return;
        }
        !isDisabled && handlePlaybackControlsClick(VIDEO_PLAYER_CONTROL_TYPES.SKIP_TO_FIRST);
      }
    }, {
      keys: ['Mod', 'ArrowRight'],
      onAction: e => {
        e.preventDefault();
        if (isMultiVideoIndex) {
          return;
        }
        !isDisabled && handlePlaybackControlsClick(VIDEO_PLAYER_CONTROL_TYPES.SKIP_TO_LAST);
      }
    }, {
      keys: ['Shift', 'ArrowLeft'],
      onAction: e => {
        e.preventDefault();
        if (isMultiVideoIndex) {
          return;
        }
        !isDisabled && handlePlaybackControlsClick(VIDEO_PLAYER_CONTROL_TYPES.JUMP_BACKWARD);
      }
    }, {
      keys: ['Shift', 'ArrowRight'],
      onAction: e => {
        e.preventDefault();
        if (isMultiVideoIndex) {
          return;
        }
        !isDisabled && handlePlaybackControlsClick(VIDEO_PLAYER_CONTROL_TYPES.JUMP_FORWARD);
      }
    }]);
    errors.forEach(error => handleShortcutRegistrationError(error));
    return () => {
      removeHotKeys();
    };
  }, [registerHotKeys, togglePlayback, isPlaying, isDisabled, handlePlaybackControlsClick, isMultiVideoIndex]);
  return <Controls>
      <HiddenMeasure ref={maxFrameMeasureRef} data-testid="frame-hidden-measure">
        <StyledLabelMedium>{maxFramesMax}</StyledLabelMedium>
      </HiddenMeasure>
      <HiddenMeasure ref={maxTimeMeasureRef} data-testid="time-hidden-measure">
        <StyledLabelMedium>{maxTimeMax}</StyledLabelMedium>
      </HiddenMeasure>
      <FramesControlPanelSection>
        <VideoControls>
          {!isCompactMode && !isMultiVideoIndex && skipToFirstButton()}

          {!isMultiVideoIndex && previousFrameButton()}

          {!isCompactMode && (showPlayPauseButton ? playPauseButton() : loadingButton())}

          {!isMultiVideoIndex && nextFrameButton()}

          {!isCompactMode && !isMultiVideoIndex && skipToLastButton()}
        </VideoControls>

        {!isCompactMode && frameSlider()}
      </FramesControlPanelSection>

      <FramesControlPanelSection>
        {!isCompactMode && !isMultiVideoIndex && frameTimeInputSwitcher()}

        {!isMultiVideoIndex && frameTimeInput()}

        {!isCompactMode && volumeSlider()}

        {!isCompactMode && speedSelect()}

        {!isCompactMode && !isMultiVideoIndex && expandContractTimelineButton()}
      </FramesControlPanelSection>
    </Controls>;
};